import { Menu, ConfigProvider, theme } from 'antd';
import type { MenuProps } from 'antd';
import { themeConfig } from '@acast-tech/echo-theme';
import { getCurrentYear } from '@/utils/dateUtils';
import type { SelectedNetwork } from '@/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useHasHlsEnabledNetwork from '@/hooks/useHasHlsEnabledNetwork';
import useEchoTopNavStickyOffset, {
  ECHO_TOPNAV_HEIGHT,
  ECHO_TOPNAV_STICKY_TRANSITION,
} from '@/hooks/useEchoTopNavStickyOffset';
import { RouteBuilder } from '@/utils/routeUtils';

interface EchoSubNavProps {
  items: MenuProps['items'];
  selectedKey?: string;
}

const EchoSubNav = ({ items, selectedKey }: EchoSubNavProps) => {
  const { token } = theme.useToken();
  const menuHeight = token.controlHeight + token.paddingContentVertical * 2;

  const styledItems: MenuProps['items'] = items?.map((item) => {
    if (!item || !('label' in item)) return item;
    return {
      ...item,
      label: <span>{item.label}</span>,
    };
  });

  return (
    <Menu
      mode="horizontal"
      selectedKeys={selectedKey ? [selectedKey] : []}
      items={styledItems}
      style={{
        height: menuHeight,
        lineHeight: `${menuHeight}px`,
        padding: `0 ${token.paddingSM}px`,
        borderBottom: `${token.lineWidth}px solid ${token.colorSplit}`,
        background: token.purple1,
      }}
    />
  );
};

const SubNavigation = ({ selectedNetwork }: { selectedNetwork?: SelectedNetwork }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    audienceTab = false,
    contentPerformance = false,
    contentPerformanceHls = false,
  } = useFlags<{
    audienceTab?: boolean;
    contentPerformance?: boolean;
    contentPerformanceHls?: boolean;
  }>();
  const hasHlsNetwork = useHasHlsEnabledNetwork();
  const { isTopNavHidden } = useEchoTopNavStickyOffset();

  // Guard on the id (not just the object) so loading/no-access states don't produce a broken
  // `?networks=` link.
  const contentPerformancePath = selectedNetwork?.id
    ? RouteBuilder.contentPerformanceTab(selectedNetwork.id)
    : undefined;

  const isContentPerformanceEnabled =
    contentPerformance || (contentPerformanceHls && hasHlsNetwork);
  const showInsightsTabs = audienceTab || isContentPerformanceEnabled;

  const navItems = [
    ...(showInsightsTabs
      ? [
          {
            key: 'content-performance',
            label: 'Content performance',
            path: contentPerformancePath,
          },
          {
            key: 'audience',
            label: 'Audience',
            path: selectedNetwork ? `/${selectedNetwork.id}/audience/` : undefined,
          },
        ]
      : []),
    {
      key: 'earnings',
      label: 'Realtime earnings',
      path: selectedNetwork ? `/${selectedNetwork.id}/earnings/` : undefined,
    },
    {
      key: 'payouts',
      label: 'Payouts',
      path: selectedNetwork ? `/${selectedNetwork.id}/payouts/${getCurrentYear()}` : undefined,
    },
  ];

  const selectedKey = navItems.find(({ key }) => location.pathname.includes(key))?.key;
  const antdItems: MenuProps['items'] = navItems.map(({ key, label, path }) => ({
    key,
    label,
    onClick: () => {
      if (path) navigate(path);
    },
  }));

  return (
    <div
      style={{
        position: 'sticky',
        top: ECHO_TOPNAV_HEIGHT,
        zIndex: 9,
        transform: isTopNavHidden ? `translateY(-${ECHO_TOPNAV_HEIGHT}px)` : 'translateY(0)',
        transition: ECHO_TOPNAV_STICKY_TRANSITION,
      }}
    >
      <ConfigProvider theme={themeConfig}>
        <EchoSubNav items={antdItems} selectedKey={selectedKey} />
      </ConfigProvider>
    </div>
  );
};

export default SubNavigation;
